/**
 * **Shared side-liveness predicate** — the ONE definition of "no living actor on an
 * allegiance (ejected-pilot-still-alive counts)"; **do not fork it.** Authored at a
 * NEUTRAL top-level home (not nested inside `sitrep/`) precisely because it isn't
 * sitrep-specific: `sitrepStatus`'s unset-`sitrep` (deathmatch) branch composes it,
 * and the autoresolution runner's termination check imports it directly for the exact
 * same verdict rather than authoring a second copy.
 *
 * **"Living actor" = {@link isActor} AND {@link isAble}:**
 *   - {@link isActor} (`@lancer-vtt/domain`) — `kind` ∈ `mech`/`npc`/`pilot`, the
 *     activation lane's own "takes a turn" predicate. A `drone` (a character that
 *     never gets a turn) or an `object`/`deployable` never counts, even if it's the
 *     last thing standing — RAW deathmatch is about whether a side can still FIGHT
 *     BACK on its own turn, not whether inert scenery survives.
 *   - {@link isAble} (`@lancer-vtt/domain`) — `hp > 0` AND not `Destroyed` AND not
 *     `Unpiloted`, the SAME "can this thing still act" predicate
 *     `roundComplete`/`ableUnactedOnSide` read. A mech reduced to a wreck
 *     (the `Destroyed` status) drops out even at `hp > 0` — Structure damage
 *     overflows HP back to full, so a destroyed unit routinely reads `hp > 0` and
 *     would otherwise falsely count as "still alive."
 *
 * **The ejected-pilot nuance falls out for free, with zero special-casing:** when a
 * mech is destroyed and its pilot ejects (`pilotBridge.ts`'s `resolvePilotEjections`),
 * the pilot becomes a SEPARATE `kind: "pilot"` {@link Combatant} with its own `hp`/
 * statuses, frozen `allegiance` matching the mech's side. That combatant is checked
 * independently by {@link livingActors} — if it's alive and un-Destroyed, it passes
 * {@link isActor} + {@link isAble} on its own merits, so "mech destroyed, pilot
 * ejected and alive on foot" correctly reads as "side NOT eliminated" without this
 * module needing to know anything about the eject mechanic at all.
 */
import { isAble, isActor, sideOf, type Combatant, type EncounterState, type Side } from "@lancer-vtt/domain";

/** Every combatant on `side` that is both an {@link isActor actor} and {@link isAble}. */
export function livingActors(state: EncounterState, side: Side): Combatant[] {
  return Object.values(state.combatants).filter((c) => sideOf(c) === side && isActor(c) && isAble(c));
}

/** Whether `side` has no living actor left — the deathmatch termination fact `sitrepStatus` and the autoresolution runner read. */
export function sideEliminated(state: EncounterState, side: Side): boolean {
  return livingActors(state, side).length === 0;
}
